import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { AutomationRecommendationStateTransport } from '~/shared/data-contract/profile-task';

import type { WorkTaskAutomation, WorkTaskAutomationRecommendation } from '../../../data-contract/task-management';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from '../../api/query-keys';
import { useAutomationRecommendationSource } from './automation-recommendation-source';

// The payload is large and only changes when the task is reassessed, so reopening a task reads it from cache.
const RECOMMENDATION_CACHE_TIME_MS = 10 * 60 * 1000;

/**
 * Recommendations ship behind the recipes flag; without it no surface shows them and none is read. A provided source
 * decides on its own.
 */
export const useAutomationRecommendationsEnabled = (): boolean => {
    const source = useAutomationRecommendationSource();
    const isFlagEnabled =
        useApplicationContext().applicationState.featureFlagService.isFeatureFlagEnabled('task-automation-recipes');

    return source?.isEnabled ?? isFlagEnabled;
};

/**
 * Read on demand by the opened task details, and only once the task's recommendation succeeded;
 * pending, failed or never requested recommendations have nothing to read.
 */
export const useWorkTaskAutomationRecommendation = ({
    state,
    taskId,
}: {
    readonly state: AutomationRecommendationStateTransport | undefined;
    readonly taskId: string;
}): UseQueryResult<WorkTaskAutomationRecommendation, Error> => {
    const { fetchWorkTaskAutomationRecommendation } = useApplicationFetchers();
    const source = useAutomationRecommendationSource();
    const isEnabled = useAutomationRecommendationsEnabled();

    return useQuery({
        enabled: isEnabled && state === 'succeeded',
        queryFn: async ({ signal }) => {
            if (source !== null) {
                return source.fetchRecommendation({ signal, taskId });
            }

            const result = await fetchWorkTaskAutomationRecommendation({ taskId });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: source?.queryKey(taskId) ?? jobArchitectureKeys.workTaskAutomationRecommendation(taskId),
        gcTime: RECOMMENDATION_CACHE_TIME_MS,
        staleTime: RECOMMENDATION_CACHE_TIME_MS,
        throwOnError: false,
    });
};

export type AutomationRecommendationDisplay = 'hidden' | 'loading' | 'ready';

/**
 * Pending recommendations and the first read of a succeeded one show the loading skeleton; once
 * data is cached it stays on screen through background refetches, even ones that fail.
 */
export const toAutomationRecommendationDisplay = ({
    data,
    isError,
    state,
}: {
    readonly data: WorkTaskAutomationRecommendation | undefined;
    readonly isError: boolean;
    readonly state: AutomationRecommendationStateTransport | undefined;
}): AutomationRecommendationDisplay => {
    if (state === 'pending') {
        return 'loading';
    }

    if (state !== 'succeeded') {
        return 'hidden';
    }

    if (data !== undefined) {
        return data.status === 'succeeded' ? 'ready' : 'hidden';
    }

    return isError ? 'hidden' : 'loading';
};

/** The automation a tile shows, or undefined once the recommendation no longer lists it. */
export const findRecommendedAutomation = (
    recommendation: WorkTaskAutomationRecommendation | undefined,
    automationId: string,
): WorkTaskAutomation | undefined =>
    recommendation?.status === 'succeeded'
        ? recommendation.automations.find((automation) => automation.automationId === automationId)
        : undefined;

/**
 * One automation of a task's succeeded recommendation, read from the same cache as the task's tile; none without a
 * task. Loading only while it is actually fetched, so a disabled read never reads as loading.
 */
export const useRecommendedAutomation = ({
    automationId,
    taskId,
}: {
    readonly automationId: string;
    readonly taskId: string | undefined;
}): { readonly automation: WorkTaskAutomation | undefined; readonly isLoading: boolean } => {
    const query = useWorkTaskAutomationRecommendation({
        state: taskId === undefined ? undefined : 'succeeded',
        taskId: taskId ?? '',
    });

    return { automation: findRecommendedAutomation(query.data, automationId), isLoading: query.isLoading };
};
